import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveUrl } from "@/lib/api";

const NavBtn = ({ onClick, label, testId, className, children }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-label={label}
        className={`grid h-12 w-12 place-items-center border border-white/20 bg-tl-dark/70 text-white transition-colors hover:border-tl-orange hover:text-tl-orange ${className}`}
    >
        {children}
    </button>
);

export const Lightbox = ({ photos, index, onClose, onMove }) => {
    const photo = index != null ? photos[index] : null;

    useEffect(() => {
        if (!photo) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") onMove(1);
            if (e.key === "ArrowLeft") onMove(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [photo, onClose, onMove]);

    return (
        <AnimatePresence>
            {photo && (
                <motion.div
                    data-testid="lightbox"
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-tl-dark3/95 p-4 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <NavBtn onClick={onClose} label="Chiudi" testId="lightbox-close" className="absolute right-4 top-4">
                        <X size={20} />
                    </NavBtn>
                    <NavBtn
                        onClick={(e) => { e.stopPropagation(); onMove(-1); }}
                        label="Foto precedente"
                        testId="lightbox-prev"
                        className="absolute left-4 top-1/2 -translate-y-1/2"
                    >
                        <ChevronLeft size={22} />
                    </NavBtn>
                    <NavBtn
                        onClick={(e) => { e.stopPropagation(); onMove(1); }}
                        label="Foto successiva"
                        testId="lightbox-next"
                        className="absolute right-4 top-1/2 -translate-y-1/2"
                    >
                        <ChevronRight size={22} />
                    </NavBtn>
                    <motion.figure
                        key={photo.id}
                        className="max-h-full max-w-6xl"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            data-testid="lightbox-image"
                            src={resolveUrl(photo.url)}
                            alt={photo.title || "Foto TECNO-LAV"}
                            className="max-h-[80vh] w-auto max-w-full object-contain"
                        />
                        <figcaption className="mt-4 flex items-center justify-between gap-4 font-mono-tl text-[11px] uppercase tracking-[0.25em] text-white/60">
                            <span data-testid="lightbox-title">{photo.title || "TECNO-LAV"}</span>
                            <span className="text-tl-orange">
                                {String(index + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
                            </span>
                        </figcaption>
                    </motion.figure>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
